/**
 * レビューの指摘の見た目（重さ・観点・出自）。
 *
 * 未解決の指摘を操作する節（`ReviewActions`）と、片付いた指摘の経緯を出す節
 * （`ReviewHistory`）の両方が同じ印を出す。**節ごとに書き分けない**——同じ指摘が
 * 2 か所で違う色・違う呼び名で出ると、読む人は別のものだと思う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビューの指摘の重さ・観点・出自を、未解決の節と経緯の節で同じ見た目で出す
 * @graph-connects resolveDisplayNameByEmail [calls] 指摘を出した人の表示名の解決
 */

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { resolveDisplayNameByEmail } from '../../../lib/displayName.js';

/**
 * 重さの見出し。**`critical` のような語をそのまま出さない。**
 *
 * 読む人が知りたいのは「放っておくとどうなるか」で、それは重さの定義そのもの
 * （critical＝このままでは作れない／major＝作ると手戻りが出る／minor＝読みやすさ）。
 * 記号を覚えさせるより、定義を出すほうが早い。
 * @graph-connects none
 */
const SEVERITY: Record<string, { labelKey: string; className: string }> = {
  critical: {
    labelKey: 'spec.review.severity.critical',
    className:
      'bg-[var(--color-error-solid)] text-[var(--color-error-text)] border-[var(--color-error)]',
  },
  major: {
    labelKey: 'spec.review.severity.major',
    className:
      'bg-[var(--color-warning-solid)] text-[var(--color-warning-text)] border-[var(--color-warning)]',
  },
  minor: {
    labelKey: 'spec.review.severity.minor',
    className:
      'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
  },
};

/**
 * 出自の見た目。**AI と人を色で分ける。**
 *
 * DLE の差し戻しは観点と重さを固定で送る（`eng` / `major`）ので、文字だけでは AI の指摘と
 * 区別できない。混ざったまま並ぶと DLE の指摘が AI の指摘に埋もれるため、人の指摘を
 * 前に出す色にする。
 * @graph-connects none
 */
const FOUND_BY_CLASS: Record<string, string> = {
  ai: 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] border-[var(--color-border)]',
  dle: 'bg-[var(--color-accent-primary)] text-white border-[var(--color-accent-primary)]',
};

/**
 * 観点の見出しの翻訳キー。どの目で見て出た指摘かが分かると、直し方の見当が付く。
 * 鍵は API が返す観点の値そのもの（識別子なので訳さない）。
 * @graph-connects none
 */
const PERSPECTIVE_KEY: Record<string, string> = {
  pl: 'spec.review.perspective.pl',
  pdm: 'spec.review.perspective.pdm',
  impact: 'spec.review.perspective.impact',
  eng: 'spec.review.perspective.eng',
  density: 'spec.review.perspective.density',
};

/**
 * 指摘の扱いの翻訳キー。**見送りと取り消しを 1 つにしない**（見送りは「指摘は正しいが
 * この案件では問題にならない」、取り消しは「指摘そのものが誤りだった」）。
 * @graph-connects none
 */
const RESOLUTION_KEY: Record<string, string> = {
  fixed: 'spec.review.resolution.fixed',
  dismissed: 'spec.review.resolution.dismissed',
  withdrawn: 'spec.review.resolution.withdrawn',
};

/**
 * 重さの呼び名。知らない値は API の返した値をそのまま出す（訳せない値を隠さない）。
 * @graph-business レビューの指摘の重さを「放っておくとどうなるか」の言葉で返す
 * @graph-connects none
 */
export function severityLabel(t: TFunction, severity: string): string {
  const labelKey = SEVERITY[severity]?.labelKey;
  return labelKey === undefined ? severity : t(labelKey);
}

/**
 * 観点の呼び名。知らない値は API の返した値をそのまま出す（訳せない値を隠さない）。
 * @graph-business レビューの指摘がどの観点で出たかを返す
 * @graph-connects none
 */
export function perspectiveLabel(t: TFunction, perspective: string): string {
  const labelKey = PERSPECTIVE_KEY[perspective];
  return labelKey === undefined ? perspective : t(labelKey);
}

/**
 * 指摘の扱いの呼び名。知らない値は API の返した値をそのまま出す。
 * @graph-business 片付いた指摘が「直した / 見送り / 取り消し」のどれで片付いたかを返す
 * @graph-connects none
 */
export function resolutionLabel(t: TFunction, resolution: string): string {
  const labelKey = RESOLUTION_KEY[resolution];
  return labelKey === undefined ? resolution : t(labelKey);
}

/**
 * 指摘を出した相手の呼び名。**役が分かっている指摘にだけ使う**（判定は `FoundByBadge` が持つ）。
 *
 * 人の名前はニックネームに寄せる（`docs/guidelines/frontend.md`「ユーザー名はニックネーム優先」）。
 * 役だけ来て誰が出したかが空なら、役だけ出す。
 * @graph-connects resolveDisplayNameByEmail [calls] 指摘を出した人の表示名の解決
 */
function foundByLabel(
  t: TFunction,
  finding: { foundByRole: string; foundBy?: string },
  nicknameByEmail: Map<string, string>,
): string {
  if (finding.foundByRole === 'ai') return t('spec.review.foundBy.ai');
  const email = (finding.foundBy ?? '').trim();
  return email === ''
    ? t('spec.review.foundBy.dle')
    : resolveDisplayNameByEmail(nicknameByEmail, { email });
}

/**
 * 操作した人の呼び名。**AI の提出は `ai:` が前に付く**ので、人の名前ではなく AI として出す
 * （`ai:` を外して誰のセッションで走ったかを出すと、AI の指摘を人が出したように読める）。
 * @graph-business レビューを操作した相手が AI か人かを見分けられる呼び名にする
 * @graph-connects resolveDisplayNameByEmail [calls] 操作した人の表示名の解決
 */
export function actorLabel(
  t: TFunction,
  actor: string,
  nicknameByEmail: Map<string, string>,
): string {
  const trimmed = actor.trim();
  if (trimmed.startsWith('ai:')) return t('spec.review.foundBy.ai');
  return trimmed === '' ? '' : resolveDisplayNameByEmail(nicknameByEmail, { email: trimmed });
}

/**
 * 重さの印。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビューの指摘の重さを色付きの印で出す
 * @graph-connects severityLabel [calls] 重さの呼び名
 */
export function SeverityBadge({ severity }: { severity: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full border ${
        SEVERITY[severity]?.className ??
        'bg-[var(--color-bg-elevated)] border-[var(--color-border)]'
      }`}
    >
      {severityLabel(t, severity)}
    </span>
  );
}

/**
 * 指摘の出自の印。**役が記録されていない指摘には印そのものを出さない。**
 *
 * 「不明」と出すと、出自を記録していなかった古い指摘と、出自が取れなかった指摘が同じ
 * 見え方になる。出さなければ「まだ分からない」として読める。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビューの指摘を AI が出したか DLE が出したかを色と名前で見分けられるようにする
 * @graph-connects foundByLabel [calls] 指摘を出した相手の呼び名
 */
export function FoundByBadge({
  finding,
  nicknameByEmail,
}: {
  finding: { foundByRole?: string; foundBy?: string };
  nicknameByEmail: Map<string, string>;
}) {
  const { t } = useTranslation();
  const { foundByRole } = finding;
  if (foundByRole === undefined) return null;
  return (
    <span
      data-testid="finding-origin"
      className={`text-xs px-2 py-0.5 rounded-full border ${
        FOUND_BY_CLASS[foundByRole] ?? 'bg-[var(--color-bg-elevated)] border-[var(--color-border)]'
      }`}
    >
      {foundByLabel(t, { ...finding, foundByRole }, nicknameByEmail)}
    </span>
  );
}
